// Menu SEFAZ: status do serviço, buscar protocolo, inutilização e situação do contribuinte.
import React, { useEffect, useState } from "react";
import { fiscal } from "../api";
import { Aviso, Botao, Campo, Cartao, Entrada, Selecao, Tabela, Texto } from "../components/ui";
import { dataBR, documentoValido, mascaraDocumento, soDigitos, UFS } from "../utils";

export default function Sefaz() {
  return (
    <div className="space-y-4">
      <h1 className="text-xl font-semibold">SEFAZ</h1>
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <StatusServico />
        <BuscarProtocolo />
      </div>
      <Contribuinte />
      <Inutilizacoes />
    </div>
  );
}

function StatusServico() {
  const [modelo, setModelo] = useState("55");
  const [r, setR] = useState(null);
  const [erro, setErro] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const consultar = async () => {
    setCarregando(true);
    setErro(null);
    try {
      setR(await fiscal.statusSefaz(modelo));
    } catch (e) {
      setErro(e.message);
    } finally {
      setCarregando(false);
    }
  };
  return (
    <Cartao titulo="Consultar status da SEFAZ">
      <div className="flex items-end gap-2">
        <Campo rotulo="Serviço" className="w-40"><Selecao value={modelo} onChange={(e) => setModelo(e.target.value)} opcoes={[["55", "NF-e"], ["65", "NFC-e"]]} /></Campo>
        <Botao carregando={carregando} onClick={consultar}>Consultar</Botao>
      </div>
      {erro && <div className="mt-3"><Aviso tipo="erro">{erro}</Aviso></div>}
      {r && (
        <div className="mt-3">
          <Aviso tipo={r.online ? "sucesso" : "atencao"}>
            {r.online ? "Serviço em operação" : "Serviço com problema"} — [{r.codigo_status}] {r.motivo}
            {"\n"}UF {r.uf} · {r.ambiente} · {dataBR(r.consultado_em, true)}
          </Aviso>
        </div>
      )}
    </Cartao>
  );
}

function BuscarProtocolo() {
  const [numero, setNumero] = useState("");
  const [modelo, setModelo] = useState("55");
  const [r, setR] = useState(null);
  const [erro, setErro] = useState(null);
  const buscar = async () => {
    setErro(null);
    setR(null);
    try {
      const lista = await fiscal.notas({ modelo, numero, por_pagina: 1 });
      const nota = lista.notas?.[0];
      if (!nota) throw new Error("Nota não encontrada");
      setR(await fiscal.buscarProtocolo(nota.id));
    } catch (e) {
      setErro(e.message);
    }
  };
  return (
    <Cartao titulo="Buscar protocolo de autorização">
      <p className="mb-2 text-xs text-gray-500">Use quando a nota ficou “processando” (queda de internet, demora da SEFAZ).</p>
      <div className="flex items-end gap-2">
        <Campo rotulo="Tipo" className="w-28"><Selecao value={modelo} onChange={(e) => setModelo(e.target.value)} opcoes={[["55", "NF-e"], ["65", "NFC-e"]]} /></Campo>
        <Campo rotulo="Número" className="w-32"><Entrada type="number" value={numero} onChange={(e) => setNumero(e.target.value)} /></Campo>
        <Botao disabled={!numero} onClick={buscar}>Buscar</Botao>
      </div>
      {erro && <div className="mt-3"><Aviso tipo="erro">{erro}</Aviso></div>}
      {r && <div className="mt-3"><Aviso tipo={r.status === "autorizada" ? "sucesso" : "atencao"}>Status: {r.status}{r.protocolo ? ` · Protocolo ${r.protocolo}` : ""}{r.motivo ? `\n${r.motivo}` : ""}</Aviso></div>}
    </Cartao>
  );
}

function Contribuinte() {
  const [doc, setDoc] = useState("");
  const [uf, setUf] = useState("");
  const [r, setR] = useState(null);
  const [carregando, setCarregando] = useState(false);
  const consultar = async () => {
    setCarregando(true);
    try {
      setR(await fiscal.contribuinte(soDigitos(doc), uf || undefined));
    } catch (e) {
      setR({ erro: e.message });
    } finally {
      setCarregando(false);
    }
  };
  const sefaz = r?.sefaz;
  return (
    <Cartao titulo="Consultar situação do contribuinte">
      <div className="flex flex-wrap items-end gap-2">
        <Campo rotulo="CPF/CNPJ" className="w-56"><Entrada value={mascaraDocumento(doc)} onChange={(e) => setDoc(soDigitos(e.target.value))} /></Campo>
        <Campo rotulo="UF" className="w-24"><Selecao value={uf} vazio="Minha" onChange={(e) => setUf(e.target.value)} opcoes={UFS.map((u) => [u, u])} /></Campo>
        <Botao carregando={carregando} disabled={!documentoValido(doc)} onClick={consultar}>Consultar</Botao>
      </div>
      {r?.erro && <div className="mt-3"><Aviso tipo="erro">{r.erro}</Aviso></div>}
      {r && !r.erro && (
        <div className="mt-3 grid grid-cols-1 gap-3 text-sm md:grid-cols-2">
          <div className="rounded border p-3 border-gray-200">
            <h4 className="mb-1 font-semibold">Receita Federal</h4>
            {r.receita ? (
              <>
                <p>{r.receita.razao_social}</p>
                <p>Situação: <strong>{r.receita.situacao_cadastral || "—"}</strong></p>
              </>
            ) : <p className="text-gray-500">{r.receita_erro || "Não se aplica (CPF)"}</p>}
          </div>
          <div className="rounded border p-3 border-gray-200">
            <h4 className="mb-1 font-semibold">SEFAZ ({r.uf})</h4>
            {sefaz ? <pre className="whitespace-pre-wrap text-xs">{JSON.stringify(sefaz.infCad || sefaz, null, 2)}</pre>
              : <p className="text-gray-500">{r.sefaz_erro || "Sem retorno"}</p>}
          </div>
        </div>
      )}
    </Cartao>
  );
}

function Inutilizacoes() {
  const [lista, setLista] = useState([]);
  const [f, setF] = useState({ modelo: "55", serie: 1, numero_inicial: "", numero_final: "", justificativa: "" });
  const [msg, setMsg] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const carregar = () => fiscal.inutilizacoes().then(setLista).catch(() => {});
  useEffect(() => { carregar(); }, []);
  const set = (c, v) => setF((x) => ({ ...x, [c]: v }));

  const enviar = async () => {
    setEnviando(true);
    setMsg(null);
    try {
      const r = await fiscal.inutilizar({ ...f, serie: Number(f.serie), numero_inicial: Number(f.numero_inicial),
        numero_final: Number(f.numero_final || f.numero_inicial) });
      setMsg({ tipo: r.status === "erro" ? "erro" : "sucesso", texto: `${r.status}${r.protocolo ? ` · protocolo ${r.protocolo}` : ""}${r.motivo ? ` — ${r.motivo}` : ""}` });
      carregar();
    } catch (e) {
      setMsg({ tipo: "erro", texto: e.message });
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Cartao titulo="Inutilização de numeração">
      <p className="mb-3 text-sm text-gray-600">Use para números que foram pulados ou ficaram rejeitados e não serão mais usados.</p>
      <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
        <Campo rotulo="Tipo"><Selecao value={f.modelo} onChange={(e) => set("modelo", e.target.value)} opcoes={[["55", "NF-e"], ["65", "NFC-e"]]} /></Campo>
        <Campo rotulo="Série"><Entrada type="number" value={f.serie} onChange={(e) => set("serie", e.target.value)} /></Campo>
        <Campo rotulo="Número inicial"><Entrada type="number" value={f.numero_inicial} onChange={(e) => set("numero_inicial", e.target.value)} /></Campo>
        <Campo rotulo="Número final"><Entrada type="number" value={f.numero_final} placeholder={f.numero_inicial} onChange={(e) => set("numero_final", e.target.value)} /></Campo>
        <Campo rotulo="Justificativa" className="col-span-2" dica="Mínimo 15 caracteres">
          <Texto rows={1} value={f.justificativa} onChange={(e) => set("justificativa", e.target.value)} />
        </Campo>
      </div>
      <div className="mt-3 flex items-center gap-3">
        <Botao variante="perigo" carregando={enviando} disabled={!f.numero_inicial || f.justificativa.trim().length < 15} onClick={enviar}>Inutilizar</Botao>
        {msg && <Aviso tipo={msg.tipo}>{msg.texto}</Aviso>}
      </div>
      <div className="mt-4">
        <Tabela
          linhas={lista}
          chave={(l) => l.id}
          vazio="Nenhuma inutilização"
          colunas={[
            { id: "criado_em", rotulo: "Data", render: (l) => dataBR(l.criado_em, true) },
            { id: "modelo", rotulo: "Tipo", render: (l) => (l.modelo === "65" ? "NFC-e" : "NF-e") },
            { id: "serie", rotulo: "Série" },
            { id: "faixa", rotulo: "Números", render: (l) => `${l.numero_inicial} a ${l.numero_final}` },
            { id: "status", rotulo: "Status" },
            { id: "protocolo", rotulo: "Protocolo" },
            { id: "justificativa", rotulo: "Justificativa" },
          ]}
        />
      </div>
    </Cartao>
  );
}
